import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { ProductType } from "src/app/features/Product/types/ProductTypeEnum";
import { FormManager } from "../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useProductCopyMutation } from "../../services/server/ProductApi";
import type { ProductCopyEntity } from "../../types/ProductCopyEntity";
import productCategoryCopyJson from "./product-category-copy.json";
import productCopyJson from "./product-copy.json";

export const ProductCopyForm: FC<FormProps> = ({ runSubmit }) => {
    const { id, type } = useParams();

    const formDefinition = type === ProductType.product ? (productCopyJson as FormJson) : (productCategoryCopyJson as FormJson);

    const [formData, setFormData] = useState<ProductCopyEntity>({} as ProductCopyEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useProductCopyMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formCopySuccess", {
                            id: result.data.id,
                            resource: "Product",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.PRODUCTS}/${FORM_ACTION.EDIT}/${result.data.id}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<ProductCopyEntity>
                    name="ProductCopyForm"
                    formData={{ ...formData, id }}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
